import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import CategoryForm from "./CategoryForm";
import { getErrorMessage } from "../../utils/errorHandler";
import { updateCategory, getCategoryById } from "../../api/categoryService";
import FullPageLoader from "../../common/FullPageLoader";

const SEO_REFERENCE_TYPE = Object.freeze({
  CATEGORY: "Category",
});

export default function EditCategory() {
  const navigate = useNavigate();
  const location = useLocation();

  const categoryId = location?.state?.categoryId;
  const slug = location?.state?.slug;

  const [backendErrors, setBackendErrors] = useState({});
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // =========================
  // LOAD CATEGORY
  // =========================
  const loadCategory = async () => {
    try {
      setPageLoading(true);

      const response = await getCategoryById(slug);

      const category = response?.data || response;

      setCategoryData(category);
    } catch (error) {
      console.error("Fetch Category Error:", error);
      alert(getErrorMessage(error));
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (!categoryId) {
      navigate("/categories");
      return;
    }

    loadCategory();
  }, [categoryId, navigate]);

  // =========================
  // UPDATE CATEGORY + SEO
  // =========================
  const handleUpdate = async (formData) => {
    try {
      setLoading(true);
      setBackendErrors({});

      const categoryPayload = {
        name: formData.name,
        tagline: formData.tagline,
        description: formData.description,
        status: formData.status,
        hsnCode: formData.hsnCode,
        taxApplied: formData.taxApplied,
        taxId: formData.taxId,
        referenceType: SEO_REFERENCE_TYPE.CATEGORY,
        metaTitle: formData.metaTitle || formData.name || "",
        metaKeywords: Array.isArray(formData.metaKeywords)
          ? formData.metaKeywords
          : (formData.metaKeywords || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
        metaDescription: formData.metaDescription || "",
        metaImage: formData.metaImage || "",
      };

      const categoryResponse = await updateCategory(
        categoryId,
        categoryPayload,
      );

      if (
        categoryResponse?.success === false ||
        categoryResponse?.status === 422
      ) {
        setBackendErrors(categoryResponse?.error || {});
        return false;
      }

      return true;
    } catch (error) {
      console.error("Update Error:", error);

      const errorData = error?.response?.data;

      if (errorData?.status === 422 || errorData?.success === false) {
        setBackendErrors(errorData?.error || {});
        return false;
      }

      alert(getErrorMessage(error));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // PAGE LOADING
  // =========================
  if (pageLoading) {
    return <FullPageLoader text="Loading category details..." />;
  }

  const seoData = categoryData?.seo || {};

  return (
    <CategoryForm
      isEdit={true}
      loading={loading}
      onSubmit={handleUpdate}
      backendErrors={backendErrors}
      initialData={{
        // =========================
        // CATEGORY
        // =========================
        name: categoryData?.name || "",
        tagline: categoryData?.tagline || "",
        description: categoryData?.description || "",
        hsnCode: categoryData?.hsnCode || "",
        taxApplied: categoryData?.taxApplied ?? false,
        taxId: categoryData?.taxId || "",
        status: categoryData?.status || "ACTIVE",
        type: categoryData?.type || "MAIN",
        parentId:
          categoryData?.parentId?._id ||
          categoryData?.parentId?.id ||
          categoryData?.parentId ||
          "",

        // =========================
        // SEO
        // =========================
        metaTitle: seoData.metaTitle || categoryData?.metaTitle || "",
        metaKeywords: Array.isArray(seoData.metaKeywords)
          ? seoData.metaKeywords.join(", ")
          : seoData.metaKeywords || categoryData?.metaKeywords || "",
        metaDescription: seoData.metaDescription || categoryData?.metaDescription || "",
        metaImage:
          seoData.metaImage || seoData.metaImageUrl ||
          categoryData?.metaImage || categoryData?.metaImageUrl ||
          "",
        metaImagePreview:
          seoData.metaImage || seoData.metaImageUrl ||
          categoryData?.metaImage || categoryData?.metaImageUrl ||
          "",
      }}
    />
  );
}
